import React, { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import type { AIWorkoutResponse, SportType, WorkoutSegment, ZoneDefinition } from '@/types';
import type { TranslationKeys } from '@/core/i18n/translations';
import {
  buildPolarFlowWebTrainingTargetDto,
  polarDescriptionFromWorkout,
} from '@/features/export-hub/services/polarFlowWebPayload';

const POLAR_FLOW_TARGET_URL = 'https://flow.polar.com/target';
const POLAR_STORE_ACK_MS = 12_000;

interface UseCloudCalendarExportArgs {
  t: TranslationKeys;
  language: 'en' | 'pl';
  /** Fallback when raw AI JSON (`aiWorkout`) is missing after refresh / history load. */
  workoutName: string;
  aiWorkout: AIWorkoutResponse | null;
  sport: SportType;
  segments: WorkoutSegment[];
  activeZones?: ZoneDefinition[];
}

function defaultDateIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function postPolarStoreAndWaitBridgeAck(
  message: Record<string, unknown>,
  timeoutMs: number,
): Promise<{ ok: boolean; reusedTab: boolean }> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener('message', onMsg);
      reject(new Error('POLAR_STORE_ACK_TIMEOUT'));
    }, timeoutMs);

    function onMsg(event: MessageEvent) {
      const d = event.data as {
        source?: string;
        type?: string;
        ok?: boolean;
        reusedTab?: boolean;
      } | null;
      if (!d || d.source !== 'p2t-bridge' || d.type !== 'STORE_POLAR_WORKOUT_ACK') return;
      window.removeEventListener('message', onMsg);
      window.clearTimeout(timer);
      resolve({ ok: Boolean(d.ok), reusedTab: Boolean(d.reusedTab) });
    }

    window.addEventListener('message', onMsg);
    window.postMessage(message, '*');
  });
}

/**
 * Polar Flow phased targets via browser session + extension (no OAuth round-trip).
 * Uses **segments** as source of truth so export works when `aiWorkout` was not restored (e.g. F5, old history rows).
 */
export function useCloudCalendarExport({
  t,
  language,
  workoutName,
  aiWorkout,
  sport,
  segments,
  activeZones,
}: UseCloudCalendarExportArgs) {
  const [sending, setSending] = useState(false);

  const sendToCalendar = useCallback(async () => {
    if (!Array.isArray(segments) || segments.length === 0) {
      toast.error(t.polarExportNoSegments);
      return;
    }

    // First synchronous line on user gesture — avoid `noopener` in the window features string;
    // some Chromium builds return `null` when the third argument includes `noopener`.
    const polarTab = window.open('about:blank', '_blank');
    setSending(true);
    try {
      let body: Record<string, unknown>;
      try {
        const calendarDate = defaultDateIso();
        const name = (workoutName || aiWorkout?.workoutName || 'Prompt2Train').trim() || 'Prompt2Train';
        const aiForDescription =
          aiWorkout && Array.isArray(aiWorkout.steps) && aiWorkout.steps.length > 0 ? aiWorkout : null;
        body = buildPolarFlowWebTrainingTargetDto({
          workoutName: name,
          description: polarDescriptionFromWorkout(aiForDescription, 'Structured workout from Prompt2Train'),
          calendarDate,
          segments,
          zones: activeZones,
          sport,
          language,
        });
      } catch (error) {
        console.error('[Polar Export] Mapping to Polar Flow payload failed:', error);
        try {
          polarTab?.close();
        } catch {
          /* ignore */
        }
        const code = error instanceof Error ? error.message : String(error);
        if (code === 'POLAR_NO_PHASES') {
          toast.error(t.polarExportNoPhases);
        } else {
          const msg = error instanceof Error ? error.message : 'MAPPING_FAILED';
          toast.error(t.workoutSendCalendarError.replace('{message}', msg));
        }
        return;
      }

      if (typeof console !== 'undefined' && console.dir) {
        console.dir(body);
      } else if (typeof console !== 'undefined' && console.log) {
        console.log('[Polar Flow] training target payload', JSON.stringify(body));
      }

      const message = {
        type: 'STORE_POLAR_WORKOUT' as const,
        body,
        tsCreated: Date.now(),
        lang: language,
        currentLanguage: language,
        lng: language,
      };

      let storeAck: { ok: boolean; reusedTab: boolean };
      try {
        storeAck = await postPolarStoreAndWaitBridgeAck(message, POLAR_STORE_ACK_MS);
      } catch (e) {
        const isTimeout = e instanceof Error && e.message === 'POLAR_STORE_ACK_TIMEOUT';
        console.error('[Polar Export] Bridge ACK failed:', e);
        toast.error(isTimeout ? t.polarExportBridgeAckTimeout : t.workoutSendCalendarError.replace('{message}', String(e)));
        try {
          polarTab?.close();
        } catch {
          /* ignore */
        }
        return;
      }

      if (!storeAck.ok) {
        console.error('[Polar Export] Extension denied or failed STORE_POLAR_WORKOUT');
        toast.error(t.polarExportBridgeAckDenied);
        try {
          polarTab?.close();
        } catch {
          /* ignore */
        }
        return;
      }

      if (storeAck.reusedTab) {
        try {
          polarTab?.close();
        } catch {
          /* ignore */
        }
        toast.success(t.workoutSendCalendarSuccess);
        return;
      }

      if (!polarTab) {
        console.warn('[Polar Export] Popup blocked — payload stored; user must open Polar Flow manually');
        toast.custom(
          () =>
            React.createElement(
              'div',
              {
                className:
                  'max-w-sm rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-800 shadow-lg dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100',
              },
              React.createElement(
                'p',
                { className: 'mb-2 font-medium text-emerald-700 dark:text-emerald-400' },
                t.workoutSendCalendarSuccess,
              ),
              React.createElement(
                'p',
                { className: 'mb-3 leading-snug text-slate-600 dark:text-slate-300' },
                t.polarExportPopupBlockedHint,
              ),
              React.createElement(
                'a',
                {
                  href: POLAR_FLOW_TARGET_URL,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                  className:
                    'inline-flex min-h-[44px] items-center justify-center rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white hover:bg-indigo-500',
                },
                t.polarExportOpenPolarFlowLink,
              ),
            ),
          { duration: 25_000, id: 'p2t-polar-popup-blocked' },
        );
        return;
      }

      try {
        polarTab.opener = null;
      } catch {
        /* ignore */
      }
      try {
        polarTab.location.href = POLAR_FLOW_TARGET_URL;
      } catch (e) {
        console.warn('[Polar Export] Could not navigate popup to Polar Flow:', e);
      }
      try {
        polarTab.focus();
      } catch {
        /* ignore */
      }
      toast.success(t.workoutSendCalendarSuccess);
    } catch (e) {
      console.error('[Polar Export] Send pipeline failed:', e);
      const msg = e instanceof Error ? e.message : 'SEND_FAILED';
      toast.error(t.workoutSendCalendarError.replace('{message}', msg));
    } finally {
      setSending(false);
    }
  }, [aiWorkout, segments, activeZones, sport, language, t, workoutName]);

  const providerLabels = useMemo(
    () => ({
      polar_flow: t.cloudProviderPolar,
    }),
    [t],
  );

  return {
    provider: 'polar_flow' as const,
    setProvider: () => {},
    statusLoading: false,
    statusError: null,
    connections: { polar_flow: true },
    loadConnections: async () => {},
    connectProvider: async () => {},
    sending,
    sendToCalendar,
    providerLabels,
  };
}
